import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Loader2, Save, MapPin, Users, Handshake, Plus, Trash2, Lock, ExternalLink, Building2,
} from "lucide-react";
import { MediaPicker } from "@/components/cms/MediaPicker";
import { RichText, plainToHtml } from "@/components/cms/RichText";

const NAVY = "#1C4358";
const ic = "text-[15px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0";

const GLOBAL_LOCKED = [
  "Brand name & logo", "Colors & typography", "Homepage global design",
  "Master service lane structure & template", "Global compliance copy", "Master AI / Q&A rules",
];

const Badge = ({ kind }) => {
  const map = { LOCAL: ["rgba(47,125,90,0.12)", "#2F7D5A"], GLOBAL: ["rgba(28,67,88,0.10)", NAVY], HYBRID: ["rgba(200,140,20,0.14)", "#B7791F"] };
  const [bg, c] = map[kind] || map.LOCAL;
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: bg, color: c }}>{kind}</span>;
};

const Field = ({ label, value, onChange, placeholder, type = "text", testid, badge = "LOCAL" }) => (
  <div className="space-y-1.5">
    <Label className="text-[13px] font-semibold text-[#1C4358] flex items-center gap-2">{label} <Badge kind={badge} /></Label>
    <Input type={type} className={ic} value={value || ""} placeholder={placeholder} data-testid={testid} onChange={(e) => onChange(e.target.value)} />
  </div>
);

const PeopleEditor = ({ label, icon: Icon, items = [], onChange, fields, testid }) => (
  <div className="space-y-3" data-testid={testid}>
    <Label className="text-[13px] font-semibold text-[#1C4358] flex items-center gap-2"><Icon size={15} /> {label} <Badge kind="LOCAL" /></Label>
    {items.map((it, i) => {
      const upd = (k, v) => { const n = [...items]; n[i] = { ...n[i], [k]: v }; onChange(n); };
      return (
        <Card key={i} className="p-3 border-[#E6EDF2] space-y-3">
          <div className="grid sm:grid-cols-2 gap-2">
            {fields.filter((f) => !f.type).map((f) => (
              <Input key={f.key} className={ic} placeholder={f.label} value={it[f.key] || ""}
                onChange={(e) => upd(f.key, e.target.value)} />
            ))}
          </div>
          {fields.filter((f) => f.type === "media").map((f) => (
            <MediaPicker key={f.key} label={f.label} testid={`${testid}-media-${i}`}
              value={it[f.key] || ""} alt={it[f.altKey || `${f.key}_alt`] || ""}
              onUrlChange={(u) => upd(f.key, u)} onAltChange={(a) => upd(f.altKey || `${f.key}_alt`, a)} />
          ))}
          {fields.filter((f) => f.type === "richtext").map((f) => {
            const html = it[`${f.key}_html`] ?? plainToHtml(it[f.key]);
            return (
              <div key={f.key} className="space-y-1">
                <Label className="text-[12px] font-semibold text-[#1C4358]">{f.label}</Label>
                <RichText testid={`${testid}-rt-${f.key}-${i}`} value={html} placeholder={f.label}
                  onChange={(h) => upd(`${f.key}_html`, h)} />
              </div>
            );
          })}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-[13px] text-[#252221]">
              <Switch checked={it.active !== false} onCheckedChange={(v) => upd("active", v)} /> Show publicly
            </label>
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange(items.filter((_, x) => x !== i))} className="text-[#9C1E22] gap-1"><Trash2 size={14} /> Remove</Button>
          </div>
        </Card>
      );
    })}
    <Button type="button" variant="outline" size="sm" onClick={() => onChange([...items, { active: true, display_order: items.length + 1 }])} className="gap-1 border-[#D6E0E7] text-[#1C4358]"><Plus size={14} /> Add</Button>
  </div>
);

export default function LocalEditor() {
  const [franchises, setFranchises] = useState([]);
  const [lanes, setLanes] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState(null);
  const [laneOv, setLaneOv] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fr, ln] = await Promise.all([api.get("/site/my-franchises"), api.get("/site/service-lanes")]);
      setFranchises(fr.data.items || []);
      setLanes((ln.data.items || []).map((l) => ({ id: l.id, slug: l.slug, name: l.name })));
      if ((fr.data.items || []).length > 0) selectFranchise(fr.data.items[0]);
    } catch { toast.error("Could not load local settings."); }
    finally { setLoading(false); }
  }, []);

  const selectFranchise = (f) => {
    setSel(f);
    setForm({
      local_phone: f.local_phone || "", local_email: f.local_email || "", mapped_area: f.mapped_area || "",
      local_seo_title: f.local_seo_title || "", local_seo_description: f.local_seo_description || "",
      local_cta_url: f.local_cta_url || "", local_team: f.local_team || [], local_partners: f.local_partners || [],
    });
    setLaneOv(f.service_lane_overrides || {});
  };

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setLane = (slug, k, v) => setLaneOv((s) => ({ ...s, [slug]: { ...(s[slug] || {}), [k]: v } }));

  const save = async () => {
    if (!sel) return;
    setSaving(true);
    try {
      await api.put(`/site/franchise/${sel.id}/overrides`, { ...form, service_lane_overrides: laneOv });
      toast.success("Local overrides saved. Your public pages are updated.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;

  if (franchises.length === 0) {
    return (
      <div className="max-w-2xl mx-auto py-20 text-center" data-testid="local-editor-empty">
        <Building2 size={30} className="mx-auto text-[#9BB9CE]" />
        <h1 className="mt-4 text-2xl font-display font-bold text-[#1C4358]">No franchises in your scope</h1>
        <p className="mt-2 text-[#4B5563]">You don't have a franchise/territory assigned to edit local content. Ask a Super Admin to assign your scope.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pb-24" data-testid="local-editor-page">
      <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-white/95 backdrop-blur border-b border-[#E6EDF2] flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MapPin size={20} style={{ color: NAVY }} />
          <h1 className="text-xl font-display font-bold text-[#1C4358]">Local Overrides</h1>
          <Badge kind="LOCAL" />
        </div>
        <div className="flex items-center gap-2">
          {sel?.subdomain && <a href={`/?franchise=${sel.subdomain}`} target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-1.5 text-[14px] font-semibold text-[#1C4358] px-3 h-10 rounded-lg hover:bg-[#1C4358]/6">Preview <ExternalLink size={15} /></a>}
          <Button onClick={save} disabled={saving} data-testid="local-save-btn" className="gap-1.5 bg-[#9C1E22] hover:bg-[#861A1D] text-white">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </Button>
        </div>
      </div>

      {franchises.length > 1 && (
        <div className="mt-4 space-y-1.5">
          <Label className="text-[13px] font-semibold text-[#1C4358]">Editing franchise</Label>
          <Select value={sel?.id} onValueChange={(id) => selectFranchise(franchises.find((f) => f.id === id))}>
            <SelectTrigger className={ic} data-testid="franchise-select"><SelectValue /></SelectTrigger>
            <SelectContent>{franchises.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {sel && <p className="mt-3 text-[13px] text-[#4B5563]">{sel.name} · <span className="font-mono">/?franchise={sel.subdomain}</span></p>}

      {/* Global locked */}
      <Card className="mt-4 p-4 border-[#E6EDF2] bg-[rgba(28,67,88,0.03)]">
        <div className="flex items-center gap-2 text-[13px] font-bold text-[#1C4358] mb-2"><Lock size={14} /> Controlled globally (read-only)</div>
        <div className="flex flex-wrap gap-2">
          {GLOBAL_LOCKED.map((g) => <span key={g} className="text-[12px] px-2.5 py-1 rounded-full bg-white border border-[#E6EDF2] text-[#4B5563] flex items-center gap-1"><Lock size={11} /> {g}</span>)}
        </div>
      </Card>

      {form && (
        <Accordion type="multiple" defaultValue={["contact", "team"]} className="mt-4 space-y-3">
          <AccordionItem value="contact" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
            <AccordionTrigger className="font-display font-semibold text-[#1C4358]">Local contact & SEO</AccordionTrigger>
            <AccordionContent className="space-y-4 pb-5">
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Local phone" value={form.local_phone} onChange={(v) => setF("local_phone", v)} placeholder="816-555-0100" testid="local-phone" />
                <Field label="Local email" type="email" value={form.local_email} onChange={(v) => setF("local_email", v)} placeholder="kc@movingmymama.com" testid="local-email" />
              </div>
              <Field label="Mapped service area" value={form.mapped_area} onChange={(v) => setF("mapped_area", v)} placeholder="Kansas City Metro — MO & KS" testid="local-mapped-area" />
              <Field label="Local CTA / contact routing URL" value={form.local_cta_url} onChange={(v) => setF("local_cta_url", v)} placeholder="https://…" testid="local-cta-url" />
              <Field label="Local SEO title" value={form.local_seo_title} onChange={(v) => setF("local_seo_title", v)} />
              <div className="space-y-1.5"><Label className="text-[13px] font-semibold text-[#1C4358] flex items-center gap-2">Local SEO description <Badge kind="LOCAL" /></Label>
                <Textarea rows={2} className={ic} value={form.local_seo_description} onChange={(e) => setF("local_seo_description", e.target.value)} /></div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="team" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
            <AccordionTrigger className="font-display font-semibold text-[#1C4358]">Local team & partners</AccordionTrigger>
            <AccordionContent className="space-y-6 pb-5">
              <PeopleEditor label="Local team" icon={Users} items={form.local_team} onChange={(v) => setF("local_team", v)} testid="local-team-editor"
                fields={[{ key: "name", label: "Name" }, { key: "title", label: "Title" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" }, { key: "photo_url", label: "Photo", type: "media", altKey: "photo_alt" }, { key: "bio", label: "Short bio", type: "richtext" }]} />
              <PeopleEditor label="Local partners" icon={Handshake} items={form.local_partners} onChange={(v) => setF("local_partners", v)} testid="local-partners-editor"
                fields={[{ key: "name", label: "Name" }, { key: "type", label: "Type" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" }, { key: "url", label: "Website URL" }, { key: "service_area", label: "Service area" }, { key: "logo_url", label: "Logo / photo", type: "media", altKey: "logo_alt" }, { key: "blurb", label: "Short blurb", type: "richtext" }]} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="lanes" className="border border-[#E6EDF2] rounded-xl px-4 bg-white">
            <AccordionTrigger className="font-display font-semibold text-[#1C4358]">Local service lane overrides</AccordionTrigger>
            <AccordionContent className="space-y-4 pb-5">
              <p className="text-[13px] text-[#4B5563]">Override the headline/subheadline for your area, or turn a lane off locally. Global content shows when left blank.</p>
              {lanes.map((ln) => {
                const ov = laneOv[ln.slug] || {};
                return (
                  <Card key={ln.slug} className="p-3 border-[#E6EDF2] space-y-2" data-testid={`lane-override-${ln.slug}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-display font-semibold text-[#1C4358] text-[15px]">{ln.name}</span>
                      <label className="flex items-center gap-2 text-[12px] text-[#252221]">
                        <Switch checked={ov.active !== false} onCheckedChange={(v) => setLane(ln.slug, "active", v)} /> Active locally
                      </label>
                    </div>
                    <Input className={ic} placeholder="Local headline override (optional)" value={ov.hero_headline || ""} onChange={(e) => setLane(ln.slug, "hero_headline", e.target.value)} />
                    <Input className={ic} placeholder="Local subheadline override (optional)" value={ov.hero_subheadline || ""} onChange={(e) => setLane(ln.slug, "hero_subheadline", e.target.value)} />
                  </Card>
                );
              })}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
    </div>
  );
}
